import { v4 as uuidv4 } from 'uuid';

import type {
  FlatGraph,
  FlatGraphEdge,
  FlatGraphNode,
} from '@/sections/insights/queryBuilder/types/FlatGraph';
import {
  hasSource,
  hasTarget,
} from '@/sections/insights/queryBuilder/types/FlatGraph';
import {
  denormalizeFilterValues,
  normalizeFilterValues,
} from '@/sections/insights/queryBuilder/utils/normalizeInFilters';
import type { GraphQuery, NodeDetails } from '@/types';

/**
 * Converts a nested GraphQuery to a flat graph structure.
 * Assigns stable UUIDs to all nodes and edges for identity.
 *
 * Architecture:
 * - GraphQuery.node becomes root node
 * - Each NodeDetails.relationships[] item becomes an edge
 * - relationship.target becomes a child node (recursively processed)
 *
 * @param query - The nested GraphQuery to flatten
 * @returns FlatGraph with nodes/edges as Maps
 */
export function flatten(query: GraphQuery): FlatGraph {
  const nodes = new Map<string, FlatGraphNode>();
  const edges = new Map<string, FlatGraphEdge>();

  // Create root node from GraphQuery.node
  const rootId = uuidv4();
  nodes.set(rootId, {
    id: rootId,
    data: {
      labels: query.node.labels,
      label_operator: query.node.label_operator,
      sorted: query.node.sorted,
      // ENG-3855: un-nest forward IN values into the flat UI shape, symmetric to
      // the re-nesting normalizeFilterValues does in unflatten (the wire re-nests
      // on save/execute). Keeps the editor's read boundaries from garbling [[...]].
      filters: denormalizeFilterValues(query.node.filters),
      self_edges: query.node.self_edges,
      alias: query.node.alias,
    },
  });

  // Recursively process relationships starting from root
  if (query.node.relationships) {
    processRelationships(query.node.relationships, rootId, nodes, edges);
  }

  return {
    nodes,
    edges,
    rootId,
  };
}

/**
 * Recursively processes relationships and their target nodes.
 * Builds flat node and edge Maps with stable UUIDs.
 *
 * @param relationships - Array of relationship objects from NodeDetails
 * @param sourceId - UUID of the source node these relationships belong to
 * @param nodes - Accumulator Map for nodes
 * @param edges - Accumulator Map for edges
 */
function processRelationships(
  relationships: NonNullable<NodeDetails['relationships']>,
  sourceId: string,
  nodes: Map<string, FlatGraphNode>,
  edges: Map<string, FlatGraphEdge>
): void {
  for (const rel of relationships) {
    // Process target relationships (existing logic)
    if (hasTarget(rel)) {
      const targetId = uuidv4();
      nodes.set(targetId, {
        id: targetId,
        data: {
          labels: rel.target.labels,
          sorted: rel.target.sorted,
          // ENG-3855: un-nest forward IN values for the flat UI shape (see flatten).
          filters: denormalizeFilterValues(rel.target.filters),
          self_edges: rel.target.self_edges,
          alias: rel.target.alias,
        },
      });

      const edgeId = uuidv4();
      edges.set(edgeId, {
        id: edgeId,
        data: {
          label: rel.label,
          length: rel.length,
          optional: rel.optional,
          select_one: rel.select_one,
          not_exists: rel.not_exists,
          // ENG-3855: un-nest forward IN values for the flat UI shape (see flatten).
          filters: denormalizeFilterValues(rel.filters),
        },
        sourceId,
        targetId,
        direction: 'target', // ← NEW: Mark as target relationship
      });

      if (rel.target.relationships && rel.target.relationships.length > 0) {
        processRelationships(rel.target.relationships, targetId, nodes, edges);
      }
    }

    // NEW: Process source relationships
    if (hasSource(rel)) {
      const sourceNodeId = uuidv4();
      nodes.set(sourceNodeId, {
        id: sourceNodeId,
        data: {
          labels: rel.source.labels,
          sorted: rel.source.sorted,
          // ENG-3855: un-nest forward IN values for the flat UI shape (see flatten).
          filters: denormalizeFilterValues(rel.source.filters),
          self_edges: rel.source.self_edges,
          alias: rel.source.alias,
        },
      });

      const edgeId = uuidv4();
      edges.set(edgeId, {
        id: edgeId,
        data: {
          label: rel.label,
          length: rel.length,
          optional: rel.optional,
          select_one: rel.select_one,
          not_exists: rel.not_exists,
          // ENG-3855: un-nest forward IN values for the flat UI shape (see flatten).
          filters: denormalizeFilterValues(rel.filters),
        },
        sourceId: sourceNodeId, // ← Reversed: child → parent
        targetId: sourceId, // ← Current node receives the edge
        direction: 'source', // ← Mark as source relationship
      });

      if (rel.source.relationships && rel.source.relationships.length > 0) {
        processRelationships(
          rel.source.relationships,
          sourceNodeId,
          nodes,
          edges
        );
      }
    }
  }
}

/**
 * Converts a flat graph structure back to nested GraphQuery.
 * Reconstructs the tree structure from node/edge Maps.
 *
 * Architecture:
 * - Starts with rootId to get root node
 * - For each edge from source, builds relationship object
 * - Recursively builds target node with its relationships
 *
 * @param graph - The FlatGraph to convert
 * @returns Nested GraphQuery structure
 */
export function unflatten(graph: FlatGraph): GraphQuery {
  const rootNode = graph.nodes.get(graph.rootId);
  if (!rootNode) {
    throw new Error(`Root node ${graph.rootId} not found in graph`);
  }

  return {
    node: buildNodeDetails(graph.rootId, graph),
  };
}

/**
 * Recursively builds a NodeDetails object from FlatGraph.
 * Constructs relationships array by finding all edges from this node.
 *
 * @param nodeId - The UUID of the node to build
 * @param graph - The FlatGraph containing all nodes/edges
 * @returns Complete NodeDetails with nested relationships
 */
function buildNodeDetails(nodeId: string, graph: FlatGraph): NodeDetails {
  const node = graph.nodes.get(nodeId);
  if (!node) {
    throw new Error(`Node ${nodeId} not found in graph`);
  }

  // Build relationships array from edges
  const relationships = buildRelationships(nodeId, graph);

  return {
    labels: node.data.labels,
    label_operator: node.data.label_operator,
    sorted: node.data.sorted,
    // ENG-3855: normalize forward IN values to nested arrays so the backend
    // binds a Cypher list RHS (a flat array/scalar matches nothing).
    filters: normalizeFilterValues(node.data.filters),
    self_edges: node.data.self_edges,
    alias: node.data.alias,
    relationships: relationships.length > 0 ? relationships : undefined,
  };
}

/**
 * Recursively builds relationships array for a given source node.
 * Finds all edges originating from source, reconstructs target nodes.
 *
 * @param sourceId - The node to build relationships for
 * @param graph - The FlatGraph containing all nodes/edges
 * @returns Array of relationship objects in GraphQuery format
 */
function buildRelationships(
  sourceId: string,
  graph: FlatGraph
): NonNullable<NodeDetails['relationships']> {
  const relationships: NonNullable<NodeDetails['relationships']> = [];

  for (const edge of graph.edges.values()) {
    // Process target relationships (outgoing edges)
    if (edge.direction === 'target' && edge.sourceId === sourceId) {
      const targetNode = buildNodeDetails(edge.targetId, graph);

      relationships.push({
        label: edge.data.label,
        length: edge.data.length,
        optional: edge.data.optional,
        select_one: edge.data.select_one,
        not_exists: edge.data.not_exists,
        // ENG-3855: normalize forward IN values to nested Cypher list RHS.
        filters: normalizeFilterValues(edge.data.filters),
        target: targetNode, // ← Use target property
      });
    }

    // NEW: Process source relationships (incoming edges)
    if (edge.direction === 'source' && edge.targetId === sourceId) {
      const sourceNode = buildNodeDetails(edge.sourceId, graph);

      relationships.push({
        label: edge.data.label,
        length: edge.data.length,
        optional: edge.data.optional,
        select_one: edge.data.select_one,
        not_exists: edge.data.not_exists,
        // ENG-3855: normalize forward IN values to nested Cypher list RHS.
        filters: normalizeFilterValues(edge.data.filters),
        source: sourceNode, // ← Use source property
      });
    }
  }

  return relationships;
}
